import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import AppFooter from '../components/AppFooter';
import PublicHeader from '../components/PublicHeader';
import { useDocumentMeta } from '../lib/useDocumentMeta';

/**
 * Public page describing the research Precious Data undertakes and inviting
 * collaboration. Deliberately general: it describes the kind of questions we
 * care about, not specific studies or findings, so nothing here needs to be
 * kept in step with a live research programme. Linked from the footer.
 */
const Research = () => {
  useDocumentMeta({
    title: 'Research into social care and the experience of care leavers | Precious Data',
    description:
      'Precious Data carries out research to better understand social care trends and the disadvantages care leavers and different groups face. We welcome collaboration with researchers, charities and public bodies.',
    path: '/research',
  });

  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="research-page">
      <PublicHeader />

      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
            Research
          </span>
          <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98]">
            Understanding care, so it can be better
          </h1>
          <p className="text-[19px] leading-[1.5] text-ink-70">
            Alongside helping individual care leavers get their records, Precious Data
            carries out research. We want to understand what growing up in care is
            really like, where the system works, and where it lets people down.
          </p>
        </div>
      </section>

      <section className="px-7 lg:px-12 py-12">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-3">
          <Card title="Trends in social care">
            How care is changing over time: placements, moves, the support people
            receive, and how practice differs between places.
          </Card>
          <Card title="Disadvantage after care">
            The barriers care leavers face in education, housing, health, work and
            relationships, and how far they trace back to experiences in care.
          </Card>
          <Card title="Differences between groups">
            Whether outcomes and treatment differ by background, region, age or
            circumstances, so that unequal experiences are visible and not hidden.
          </Card>
        </div>
      </section>

      <Section eyebrow="How we do it" title="Mostly from public information and partnership.">
        <p>
          Care leavers have long said that the system does not always listen. Patterns
          across many people show things no single file can: where problems keep
          recurring, and where some groups are served worse than others. To see those
          patterns we draw on what is already public, or what organisations are willing
          to share with us.
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            <strong className="text-ink">Freedom of Information requests</strong> to
            councils, police forces and other public bodies, asking for the figures and
            policies that explain how care is run.
          </li>
          <li>
            <strong className="text-ink">Public data</strong>, such as published
            statistics, inspection reports and official returns.
          </li>
          <li>
            <strong className="text-ink">Working with care organisations, local
            authorities and police</strong> to understand how things work in practice and
            what would help.
          </li>
          <li>
            <strong className="text-ink">Anonymised statistics from Precious Data
            itself</strong>, as a small supporting part of the picture (see below).
          </li>
        </ul>
        <p>
          We want to share what we find in a way that helps, so organisations can see
          where to improve and care leavers can see where their experience fits a wider pattern.
        </p>
      </Section>

      <Section eyebrow="Your information" title="Your records are never our research material." tinted>
        <p>
          Precious Data holds some of the most sensitive information there is, and our
          research does not depend on it.
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Individual records are encrypted with keys only their owner holds.{' '}
            <strong className="text-ink">We cannot read them, so we cannot research
            them.</strong>
          </li>
          <li>
            Where we do use statistics from the service, they are anonymised, aggregated
            running totals, never a person&apos;s text, documents or case. Figures about a
            specific organisation or group are withheld until enough people have
            contributed that nobody could be picked out.
          </li>
          <li>
            People can see and change what they contribute in their profile privacy
            settings. Sensitive categories, such as ethnicity and disability, are off
            unless someone switches them on.
          </li>
        </ul>
        <p>
          You can read more on our{' '}
          <Link to="/privacy" className="text-brand underline hover:opacity-90">
            privacy page
          </Link>
          .
        </p>
      </Section>

      <Section eyebrow="Work with us" title="We would like to hear from you.">
        <p>
          We are keen to work with others who share the goal of understanding care
          better. That might be:
        </p>
        <ul className="space-y-2 list-disc pl-5">
          <li>Universities and independent researchers studying social care or care leavers.</li>
          <li>Charities and campaigning organisations who need evidence.</li>
          <li>Councils, police forces, care providers and other public bodies who want to learn from the experience of care leavers.</li>
          <li>Care leavers and care-experienced groups who want to help shape the questions we ask.</li>
        </ul>
        <p>
          We take collaboration seriously and will always put the safety, privacy and
          wishes of care leavers first. That may mean saying no to ideas that would
          put anyone at risk, and being clear about why.
        </p>
      </Section>

      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-3xl mx-auto space-y-5">
          <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
            Have a question or an idea?
          </h2>
          <p className="text-ink-70 leading-relaxed">
            Tell us a little about who you are and what you have in mind. A short email
            is plenty to start with.
          </p>
          <div className="pt-2">
            <Button
              asChild
              variant="secondary"
              size="lg"
              data-testid="research-contact-btn"
            >
              <a href="mailto:hello@preciousdata.org.uk?subject=Research%20enquiry">
                hello@preciousdata.org.uk
              </a>
            </Button>
          </div>
        </div>
      </section>

      <AppFooter />
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="bg-ground rounded-lg p-6 space-y-2">
    <p className="font-heading text-[19px] font-semibold tracking-[-0.02em]">{title}</p>
    <p className="text-[15px] text-ink-70 leading-relaxed">{children}</p>
  </div>
);

/** A prose section: eyebrow, page-level heading, body. `tinted` puts it on a ground panel. */
const Section = ({ eyebrow, title, tinted = false, children }) => (
  <section className={tinted ? 'mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-12 lg:px-12' : 'px-7 lg:px-12 py-12'}>
    <div className="max-w-3xl mx-auto space-y-5">
      <p className="pd-eyebrow">{eyebrow}</p>
      <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05] text-ink">
        {title}
      </h2>
      <div className="space-y-4 text-ink-70 leading-relaxed">{children}</div>
    </div>
  </section>
);

export default Research;
